import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Crosshair, Maximize2, Minimize2, Trash2 } from 'lucide-react';
import RaccoonReportModal, { type PickedPoint } from './RaccoonReportModal';
import {
  adminDeleteRaccoonCell,
  adminDeleteRaccoonReport,
  adminListRaccoonCell,
  adminDeleteRaccoonSource,
  adminListRaccoonReports,
  deleteMyRaccoonReport,
  fetchRaccoonGrid,
  listMyRaccoonReports,
  weekLabel,
  type AdminRaccoonReport,
  type MyRaccoonReport,
  type RaccoonCell,
  rangeLabel,
  type RaccoonRange,
} from '../lib/raccoon';
import './raccoon-map.css';

const DENMARK: L.LatLngBoundsExpression = [
  [54.5, 8.0],
  [57.8, 15.2],
];
const RANGES: { id: RaccoonRange; label: string; short: string }[] = [
  { id: '30d', label: 'Sidste 30 dage', short: '30 dage' },
  { id: 'year', label: 'Dette år', short: 'Dette år' },
  { id: 'all', label: 'Alle', short: 'Alle' },
];

const TABS = [
  { id: 'mine', label: 'Dine indberetninger' },
  { id: 'stats', label: 'Statistik' },
  { id: 'about', label: 'Sådan virker det' },
  { id: 'admin', label: 'Admin' },
] as const;

const HARVEST = '#8c1d2f';
const OBSERVED = '#d9730d';

interface Props {
  notify: (message: string) => void;
  isAdmin?: boolean;
}

export default function RaccoonMapPage({ notify, isAdmin = false }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const slotRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const gridRef = useRef<L.LayerGroup | null>(null);
  const pickRef = useRef<L.Marker | null>(null);
  const pickingRef = useRef(false);

  const [range, setRange] = useState<RaccoonRange>('30d');
  const [cells, setCells] = useState<RaccoonCell[]>([]);
  const [selected, setSelected] = useState<RaccoonCell | null>(null);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [picking, setPicking] = useState(false);
  const [point, setPoint] = useState<PickedPoint | null>(null);
  const [mine, setMine] = useState<MyRaccoonReport[]>([]);
  const [aerial, setAerial] = useState(false);
  const [zoom, setZoom] = useState(7);
  const [fullscreen, setFullscreen] = useState(false);
  const [tab, setTab] = useState<'mine' | 'stats' | 'about' | 'admin'>('mine');
  const [adminRows, setAdminRows] = useState<AdminRaccoonReport[]>([]);
  const [cellRows, setCellRows] = useState<AdminRaccoonReport[]>([]);

  const loadGrid = useCallback(async (r: RaccoonRange) => {
    try {
      setError('');
      setCells(await fetchRaccoonGrid(r));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Mårhundekortet kunne ikke hentes.');
    }
  }, []);

  const loadMine = useCallback(async () => {
    try {
      setMine(await listMyRaccoonReports());
    } catch {
      setMine([]);
    }
  }, []);

  const loadAdmin = useCallback(async () => {
    if (!isAdmin) return;
    try {
      setAdminRows(await adminListRaccoonReports());
    } catch {
      setAdminRows([]);
    }
  }, [isAdmin]);

  const loadCell = useCallback(
    async (cellId: string | null) => {
      if (!isAdmin || !cellId) {
        setCellRows([]);
        return;
      }
      try {
        setCellRows(await adminListRaccoonCell(cellId));
      } catch {
        setCellRows([]);
      }
    },
    [isAdmin],
  );

  useEffect(() => {
    void loadCell(selected?.cell_id ?? null);
  }, [selected?.cell_id, loadCell]);

  useEffect(() => {
    void loadAdmin();
  }, [loadAdmin]);

  useEffect(() => {
    void loadGrid(range);
  }, [range, loadGrid]);

  useEffect(() => {
    void loadMine();
  }, [loadMine]);

  useEffect(() => {
    pickingRef.current = picking;
  }, [picking]);

  // Kortet vises via en React-portal i et eget element (host). I fuldskærm flyttes host til body,
  // så hverken sidens transform, top- eller bundmenu kan dække det. Portalen sørger for, at Reacts
  // klik- og touch-hændelser stadig virker, også uden for appens rodelement.
  const [host] = useState(() => {
    const el = document.createElement('div');
    el.className = 'rk-host';
    return el;
  });
  useLayoutEffect(() => {
    const slot = slotRef.current;
    if (!slot) return;
    slot.appendChild(host);
    return () => {
      host.remove();
    };
  }, [host]);
  useLayoutEffect(() => {
    const slot = slotRef.current;
    if (!slot || !fullscreen) return;
    document.body.appendChild(host);
    return () => {
      slot.appendChild(host);
    };
  }, [fullscreen, host]);

  // Fuldskærm: kortet fylder hele skærmen, Escape lukker, og siden bagved ruller ikke.
  useEffect(() => {
    const map = mapRef.current;
    const tick = window.setTimeout(() => map?.invalidateSize(), 60);
    if (!fullscreen) return () => window.clearTimeout(tick);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !modalOpen) setFullscreen(false);
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(tick);
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [fullscreen, modalOpen]);

  // Opret kortet én gang.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, { zoomControl: true }).fitBounds(DENMARK);
    mapRef.current = map;
    gridRef.current = L.layerGroup().addTo(map);
    setZoom(map.getZoom());
    map.on('zoomend', () => setZoom(map.getZoom()));
    map.on('click', (e: L.LeafletMouseEvent) => {
      if (!pickingRef.current) return;
      setPoint({ lat: e.latlng.lat, lng: e.latlng.lng, source: 'map' });
      setPicking(false);
      setModalOpen(true);
    });
    return () => {
      map.remove();
      mapRef.current = null;
      gridRef.current = null;
    };
  }, []);

  // Baggrundskort: skift lag uden at røre ternene.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    // Kortet ligger altid nederst; luftfoto lægges oven på og dækker kun Danmark og nær zoom,
    // så der aldrig opstår hvide felter.
    const base = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      subdomains: 'abc',
      attribution: '© OpenStreetMap',
    }).addTo(map);
    base.bringToBack();
    let ortho: L.TileLayer | null = null;
    if (aerial) {
      ortho = L.tileLayer('/api/tiles/orto/v2/{z}/{x}/{y}.jpg', {
        minZoom: 8,
        maxZoom: 20,
        maxNativeZoom: 19,
      }).addTo(map);
    }
    return () => {
      base.remove();
      ortho?.remove();
    };
  }, [aerial]);

  // Tegn tern ved nye data.
  useEffect(() => {
    const group = gridRef.current;
    if (!group) return;
    group.clearLayers();
    for (const cell of cells) {
      const { south, west, north, east } = cell.cell;
      const harvested = cell.count_harvested > 0;
      const color = harvested ? HARVEST : OBSERVED;
      const rect = L.rectangle(
        [
          [south, west],
          [north, east],
        ],
        { color, weight: 1.5, fillColor: color, fillOpacity: 0.35 },
      );
      rect.on('click', (e) => {
        if (pickingRef.current) return;
        L.DomEvent.stopPropagation(e);
        setSelected(cell);
      });
      rect.addTo(group);
      const total = cell.count_harvested + cell.count_observed;
      // Ved lav zoom vises små prikker uden tal, så mange tern ikke dækker hinanden.
      const detailed = zoom >= 9;
      L.marker([(south + north) / 2, (west + east) / 2], {
        interactive: false,
        icon: L.divIcon({
          className: 'rk-badge-icon',
          html: detailed
            ? `<span class="rk-badge" style="background:${color}">${total}</span>`
            : `<span class="rk-dot" style="background:${color}"></span>`,
          iconSize: detailed ? [28, 28] : [10, 10],
        }),
      }).addTo(group);
    }
  }, [cells, zoom]);

  // Markør på valgt position.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    pickRef.current?.remove();
    pickRef.current = null;
    if (point) {
      pickRef.current = L.marker([point.lat, point.lng], {
        icon: L.divIcon({
          className: 'rk-badge-icon',
          html: '<span class="rk-pin"></span>',
          iconSize: [26, 26],
          iconAnchor: [13, 13],
        }),
      }).addTo(map);
    }
  }, [point]);

  const cursor = picking ? 'crosshair' : '';
  useEffect(() => {
    if (containerRef.current) containerRef.current.style.cursor = cursor;
  }, [cursor]);

  const totals = cells.reduce(
    (acc, c) => ({
      harvested: acc.harvested + c.count_harvested,
      observed: acc.observed + c.count_observed,
    }),
    { harvested: 0, observed: 0 },
  );
  const week = selected ? weekLabel(selected) : null;

  const periodChips = (
    <div className="rk-filters" role="group" aria-label="Periode">
      {RANGES.map((r) => (
        <button
          key={r.id}
          type="button"
          className={`rk-chip ${range === r.id ? 'is-on' : ''}`}
          onClick={() => {
            setRange(r.id);
            setSelected(null);
          }}
        >
          {r.short}
        </button>
      ))}
      <button
        type="button"
        className={`rk-chip rk-chip-aerial ${aerial ? 'is-on' : ''}`}
        onClick={() => setAerial((v) => !v)}
      >
        Luftfoto
      </button>
    </div>
  );

  return (
    <div className="rk-page">
      <header className="rk-header">
        <div>
          <h1>Mårhundekort</h1>
          <p>
            Fælles kort over observerede og nedlagte mårhunde. Kun antal i områder på 2 × 2 km vises
            – aldrig præcise positioner, klokkeslæt eller navne.
          </p>
        </div>
        {periodChips}
      </header>

      {error && (
        <p role="alert" className="rk-error">
          {error}
        </p>
      )}

      <div ref={slotRef} />
      {createPortal(
        <div className={`rk-map-wrap ${fullscreen ? 'is-full' : ''}`}>
          <div className="rk-map-area">
            <div ref={containerRef} className="rk-map" />
            {picking && (
              <div className="rk-banner" role="status">
                Tryk på kortet, hvor mårhunden var
                <button type="button" onClick={() => setPicking(false)}>
                  Annullér
                </button>
              </div>
            )}
            <div className="rk-legend">
              <span>
                <i style={{ background: OBSERVED }} /> Observeret
              </span>
              <span>
                <i style={{ background: HARVEST }} /> Nedlagt
              </span>
            </div>
            {selected && (
              <aside className="rk-info" aria-live="polite">
                <button
                  type="button"
                  className="rk-info-close"
                  onClick={() => setSelected(null)}
                  aria-label="Luk"
                >
                  ×
                </button>
                <strong>{week ?? 'Område på 2 × 2 km'}</strong>
                <span>
                  {selected.count_harvested} nedlagt · {selected.count_observed} observeret
                </span>
                {!week && <small>Tidspunkt skjules, til flere har indberettet i området.</small>}
                {isAdmin && (
                  <div className="rk-info-admin">
                    <small className="rk-info-admin-title">Admin: indberetninger i området</small>
                    <ul>
                      {cellRows.map((r) => (
                        <li key={r.id}>
                          <span>
                            {r.report_type === 'harvested' ? 'Nedlagt' : 'Observeret'} ·{' '}
                            {new Date(r.observed_on).toLocaleDateString('da-DK')}
                            {r.source ? ` · ${r.source}` : ''}
                          </span>
                          <button
                            type="button"
                            className="rk-info-del"
                            onClick={() => {
                              if (
                                !window.confirm('Slet denne indberetning? Det kan ikke fortrydes.')
                              )
                                return;
                              void adminDeleteRaccoonReport(r.id)
                                .then(() => {
                                  notify('Indberetningen er slettet.');
                                  void loadCell(selected.cell_id);
                                  void loadAdmin();
                                  void loadMine();
                                  void loadGrid(range);
                                })
                                .catch((e) =>
                                  notify(e instanceof Error ? e.message : 'Kunne ikke slette.'),
                                );
                            }}
                          >
                            Slet
                          </button>
                        </li>
                      ))}
                    </ul>
                    {cellRows.length > 1 && (
                      <button
                        type="button"
                        className="rk-info-del rk-info-del-all"
                        onClick={() => {
                          if (
                            !window.confirm(
                              `Slet alle ${cellRows.length} indberetninger i området? Det kan ikke fortrydes.`,
                            )
                          )
                            return;
                          void adminDeleteRaccoonCell(selected.cell_id)
                            .then((n) => {
                              notify(`${n} indberetninger er slettet.`);
                              setSelected(null);
                              void loadAdmin();
                              void loadMine();
                              void loadGrid(range);
                            })
                            .catch((e) =>
                              notify(e instanceof Error ? e.message : 'Kunne ikke slette.'),
                            );
                        }}
                      >
                        Slet alle i området
                      </button>
                    )}
                  </div>
                )}
              </aside>
            )}
            <button
              type="button"
              className="rk-fab"
              onClick={() => {
                setPoint(null);
                setModalOpen(true);
              }}
            >
              <Crosshair size={20} /> Indberet mårhund
            </button>
            <button
              type="button"
              className="rk-full-btn"
              onClick={() => setFullscreen((v) => !v)}
              aria-label={fullscreen ? 'Afslut fuldskærm' : 'Vis kortet i fuldskærm'}
              aria-pressed={fullscreen}
            >
              {fullscreen ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
            </button>
          </div>
          <div className="rk-bar">
            {fullscreen && periodChips}
            <p className="rk-range">{rangeLabel(range)}</p>
          </div>
        </div>,
        host,
      )}

      <nav className="rk-tabs" role="tablist" aria-label="Mere om Mårhundekortet">
        {TABS.filter((t) => t.id !== 'admin' || isAdmin).map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`rk-tab ${tab === t.id ? 'is-on' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
            {t.id === 'mine' && mine.length > 0 ? (
              <span className="rk-tab-count">{mine.length}</span>
            ) : null}
            {t.id === 'admin' && adminRows.length > 0 ? (
              <span className="rk-tab-count">{adminRows.length}</span>
            ) : null}
          </button>
        ))}
      </nav>

      {tab === 'mine' && (
        <section className="rk-panel">
          {mine.length === 0 ? (
            <p className="rk-empty">
              Du har ikke indberettet endnu. Tryk på "Indberet mårhund" på kortet for at komme i
              gang.
            </p>
          ) : (
            <ul className="rk-list">
              {mine.map((r) => (
                <li key={r.id}>
                  <span>
                    {r.report_type === 'harvested' ? 'Nedlagt' : 'Observeret'} ·{' '}
                    {new Date(r.observed_on).toLocaleDateString('da-DK')}
                    {r.photo_path ? ' · med foto' : ''}
                  </span>
                  <button
                    type="button"
                    className="rk-icon-btn"
                    aria-label="Slet indberetning"
                    onClick={() => {
                      if (!window.confirm('Slet din indberetning? Det kan ikke fortrydes.')) return;
                      void deleteMyRaccoonReport(r)
                        .then(() => {
                          notify('Indberetningen er slettet.');
                          void loadMine();
                          void loadGrid(range);
                        })
                        .catch((e) =>
                          notify(e instanceof Error ? e.message : 'Kunne ikke slette.'),
                        );
                    }}
                  >
                    <Trash2 size={18} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {tab === 'stats' && (
        <section className="rk-panel">
          <div className="rk-stats">
            <div className="rk-stat" style={{ borderTopColor: HARVEST }}>
              <strong>{totals.harvested}</strong>
              <span>Nedlagt</span>
            </div>
            <div className="rk-stat" style={{ borderTopColor: OBSERVED }}>
              <strong>{totals.observed}</strong>
              <span>Observeret</span>
            </div>
            <div className="rk-stat">
              <strong>{cells.length}</strong>
              <span>Områder (2 × 2 km)</span>
            </div>
          </div>
          <p className="rk-credit">
            Tal for den valgte periode ({RANGES.find((r) => r.id === range)?.label.toLowerCase()}).
            Skift periode over kortet.
          </p>
        </section>
      )}

      {tab === 'about' && (
        <section className="rk-panel rk-about">
          <h2>Sådan virker Mårhundekortet</h2>
          <ul>
            <li>
              Alle indberetninger er anonyme for andre. Kun antal i områder på 2 × 2 km vises.
            </li>
            <li>
              Præcis position, klokkeslæt og navne bliver aldrig vist. Uge og år vises først, når
              mindst to har indberettet i samme område.
            </li>
            <li>Du kan indberette op til 14 dage tilbage og slette dine indberetninger igen.</li>
            <li>
              Fotos renses for position og kameradata. De er kun synlige for dig og administratorer
              og regnes ikke som verificeret dokumentation.
            </li>
            <li>
              Kortet kan indeholde kendte fund fra Arter.dk via GBIF (CC BY 4.0), i områder på 2 × 2
              km.
            </li>
          </ul>
        </section>
      )}

      {isAdmin && tab === 'admin' && (
        <section className="rk-panel rk-admin">
          <p className="rk-credit">Som administrator kan du altid slette enhver indberetning.</p>
          {adminRows.some((r) => r.source === 'gbif') && (
            <button
              type="button"
              className="rk-btn rk-danger"
              onClick={() => {
                if (!window.confirm('Slet ALLE importerede fund (GBIF)? Det kan ikke fortrydes.'))
                  return;
                void adminDeleteRaccoonSource('gbif')
                  .then((n) => {
                    notify(`${n} importerede fund er slettet.`);
                    void loadAdmin();
                    void loadGrid(range);
                  })
                  .catch((e) => notify(e instanceof Error ? e.message : 'Kunne ikke slette.'));
              }}
            >
              <Trash2 size={16} /> Slet alle importerede fund (GBIF)
            </button>
          )}
          <ul className="rk-list">
            {adminRows.map((r) => (
              <li key={r.id}>
                <span title={r.email}>
                  {r.report_type === 'harvested' ? 'Nedlagt' : 'Observeret'} ·{' '}
                  {new Date(r.observed_on).toLocaleDateString('da-DK')} · {r.email}
                  {r.source ? ` · ${r.source}` : ''}
                  {r.has_photo ? ' · foto' : ''}
                </span>
                <button
                  type="button"
                  className="rk-icon-btn"
                  aria-label="Slet indberetning"
                  onClick={() => {
                    if (!window.confirm('Slet denne indberetning? Det kan ikke fortrydes.')) return;
                    void adminDeleteRaccoonReport(r.id)
                      .then(() => {
                        notify('Indberetningen er slettet.');
                        void loadAdmin();
                        void loadMine();
                        void loadGrid(range);
                      })
                      .catch((e) => notify(e instanceof Error ? e.message : 'Kunne ikke slette.'));
                  }}
                >
                  <Trash2 size={18} />
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <RaccoonReportModal
        isOpen={modalOpen && !picking}
        point={point}
        onPickOnMap={() => {
          setModalOpen(false);
          setPicking(true);
        }}
        onPointChange={setPoint}
        onClose={() => setModalOpen(false)}
        onSaved={(w) => {
          setModalOpen(false);
          setPoint(null);
          notify(`Indberetning gemt anonymt i ${w}.`);
          void loadMine();
          void loadGrid(range);
        }}
      />
    </div>
  );
}
